/**
 * Proving Ground passes and discount codes.
 *
 * A pass is a single-use code that belongs to one person and expires. Two kinds
 * exist today:
 *
 * - `proving_ground_intro` — issued by /start, covers a whole intro seat.
 * - `lvl0_upgrade` — issued when an Intro attendee pays, takes a fixed amount
 *   off the next class up the ladder.
 *
 * A code with `discount_cents` set is a discount; without it the seat is comped
 * outright. Which sessions accept which kind is data on the event, so a future
 * rung of the ladder needs no new code. All the decision-making lives here as
 * pure functions so the rules are testable without a database.
 */

export const INTRO_PASS_KIND = "proving_ground_intro";
export const LVL0_UPGRADE_KIND = "lvl0_upgrade";

/** Codes are printed and read aloud, so accept sloppy typing. */
export function normalizePassCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

/**
 * Cheap shape check used by the sign-up screens to decide whether to show the
 * seat as covered while typing. The real verdict always comes from the server.
 */
export function looksLikePassCode(raw: string): boolean {
  const c = normalizePassCode(raw);
  return /^PG-[A-Z0-9]{6}$/.test(c) || /^[A-Z][A-Z0-9]{5,15}$/.test(c);
}

export interface IntroPassRow {
  id: string;
  code: string;
  kind: string;
  pilot_id: string;
  expires_at: string;
  redeemed_at: string | null;
  event_id: string | null;
  discount_cents?: number | null;
}

export type PassRejection =
  "not_found" | "not_yours" | "wrong_kind" | "expired" | "already_used" | "event_not_eligible";

export type PassCheck =
  { ok: true; passId: string; discountCents: number } | { ok: false; reason: PassRejection };

export const PASS_MESSAGES: Record<PassRejection, string> = {
  not_found: "We don't recognise that code. Check the email we sent you.",
  not_yours:
    "That code belongs to a different email address. Use the address the code was sent to.",
  wrong_kind: "That code isn't valid for this session.",
  expired: "That code has expired. Get in touch and we'll sort you out.",
  already_used: "That code has already been used.",
  event_not_eligible: "This session doesn't accept that code.",
};

export function passMessage(reason: PassRejection): string {
  return PASS_MESSAGES[reason];
}

/** Does this session take codes of that kind? */
export function eventAcceptsPassKind(
  kind: string,
  opts: { eventIsIntroSession: boolean; acceptedKinds?: string[] | null },
): boolean {
  if ((opts.acceptedKinds ?? []).includes(kind)) return true;
  // Intro sessions have always taken the free intro pass without extra config.
  return kind === INTRO_PASS_KIND && opts.eventIsIntroSession;
}

/**
 * Decide whether a pass may be spent. `pass` is null when no row matched the
 * code at all — we never say whose it was.
 */
export function evaluatePass(
  pass: IntroPassRow | null,
  opts: {
    pilotId: string;
    eventIsIntroSession: boolean;
    acceptedKinds?: string[] | null;
    now?: Date;
  },
): PassCheck {
  const now = opts.now ?? new Date();
  if (!pass) {
    // Nothing matched: say so plainly unless this session takes no codes at all.
    return {
      ok: false,
      reason:
        opts.eventIsIntroSession || (opts.acceptedKinds ?? []).length > 0
          ? "not_found"
          : "event_not_eligible",
    };
  }
  if (!eventAcceptsPassKind(pass.kind, opts)) {
    // A session that takes no codes at all is the session's problem; otherwise
    // the code itself is the wrong kind for this night.
    const takesAnyCode = opts.eventIsIntroSession || (opts.acceptedKinds ?? []).length > 0;
    return { ok: false, reason: takesAnyCode ? "wrong_kind" : "event_not_eligible" };
  }
  if (pass.pilot_id !== opts.pilotId) return { ok: false, reason: "not_yours" };
  if (pass.redeemed_at) return { ok: false, reason: "already_used" };
  if (new Date(pass.expires_at).getTime() <= now.getTime()) {
    return { ok: false, reason: "expired" };
  }
  return { ok: true, passId: pass.id, discountCents: Math.max(0, pass.discount_cents ?? 0) };
}
